/**
 * Normalizes a connector type by stripping the `platform-` prefix if present.
 *
 * Some connector types are prefixed with `platform-` (e.g., `platform-google-drive-connector`)
 * but should be reported in analytics without this prefix (e.g., `google-drive-connector`).
 */
export const normalizeConnectorType = (connectorType: string | undefined): string | undefined => {
	if (!connectorType) {
		return connectorType;
	}

	return connectorType.startsWith('platform-')
		? connectorType.slice('platform-'.length)
		: connectorType;
};

const asOptionalString = (value: unknown): string | undefined =>
	typeof value === 'string' ? value : undefined;

/**
 * Normalizes analytics event attributes so `connectorType`/`connectorKey` are consistently
 * present (mirroring each other) and stripped of the `platform-` prefix, and so `product` is
 * likewise normalized. Unknown/non-string values for these fields are treated as absent.
 */
export const normalizeFields = (fields: Record<string, unknown>): Record<string, unknown> => {
	const { product, connectorType, connectorKey, ...data } = fields;
	const connectorValue = asOptionalString(connectorType) ?? asOptionalString(connectorKey);
	const connectorNormal = connectorValue ? normalizeConnectorType(connectorValue) : undefined;
	return {
		...data,
		product: normalizeConnectorType(asOptionalString(product)),
		connectorType: connectorNormal,
		connectorKey: connectorNormal,
	};
};
